// Industry Configurations — Alfred Voice, the Voice Executive Assistant
//
// Done-for-you (white-glove) Voice Executive Assistant that does three jobs:
//   1. Answers every call 24/7      → SAVES TIME
//   2. Completes the sale           → INCREASES REVENUE
//   3. Trains & supports the team   → REDUCES EXPENSES
// Brand name stays "Alfred Voice" / "Alfred".
//
// Copy is drawn from the AlfredVoice-v2 industry research docs and kept concise
// (black & white, drawings over icons, short per-section copy).
//
// STATS POLICY: hero stats use the verified directional set; ROI uses defensible
// customer/job-value figures from the research docs. Avoid the unverified
// missed-call percentages that failed independent verification.

import {
  Clock, Zap, TrendingUp, CheckCircle, Phone, AlertTriangle,
  Users, GraduationCap, Calendar, DollarSign, MessageSquare, ShieldCheck, Headphones
} from 'lucide-react';

const BRAND_GRADIENT = 'bg-gradient-to-br from-indigo-600 to-emerald-500'; // legacy (offline configs only)
const DEMO_PHONE = '+1 (346) 258-0006';

// Verified directional stats only — no fabricated industry percentages.
const VERIFIED_STATS = [
  { value: 'Instant', label: 'Answer time, 24/7' },
  { value: '10–15×', label: 'Phone vs. web conversion' },
  { value: '3-in-1', label: 'Calls · Sales · Team' },
  { value: 'Days', label: 'To go live' }
];

const EA_COMPARISON = {
  title: 'Why done-for-you beats the alternatives',
  columns: ['Alfred Voice', 'Answering Service', 'DIY AI Tool'],
  rows: [
    { label: 'Answers every call, 24/7', values: [true, true, true] },
    { label: 'Books the job, not a message', values: [true, false, false] },
    { label: 'Completes the sale', values: [true, false, false] },
    { label: 'Trains & supports your team', values: [true, false, false] },
    { label: 'Sounds human', values: [true, false, false] },
    { label: 'Built, tuned & launched for you', values: [true, false, false] }
  ]
};

// Freight is B2B (carrier↔broker↔driver) — different stats and comparison than consumer verticals.
const FREIGHT_STATS = [
  { value: 'Instant', label: 'Answer time, 24/7' },
  { value: '24/7', label: 'After-hours dispatch' },
  { value: '3-in-1', label: 'Offers · Check-ins · Booking' },
  { value: 'Days', label: 'To go live' }
];

const FREIGHT_COMPARISON = {
  title: 'Why done-for-you beats the alternatives',
  columns: ['Alfred Voice', 'Answering Service', 'Voicemail'],
  rows: [
    { label: 'Answers every call, 24/7', values: [true, true, false] },
    { label: 'Takes driver check-ins', values: [true, false, false] },
    { label: 'Captures after-hours load offers', values: [true, false, false] },
    { label: 'Books dock appointments', values: [true, false, false] },
    { label: 'Logs into your TMS / load board', values: [true, false, false] },
    { label: 'Live-transfers real negotiations', values: [true, false, false] }
  ]
};

const eaHowItWorks = [
  { title: 'Discovery Call', description: 'We learn your business—hours, services, and how you want calls handled.' },
  { title: 'We Build & Test It', description: 'We configure Alfred on your real call flows; you test until it’s right.' },
  { title: 'Go Live in Days', description: 'Forward your number. Alfred answers, books, and follows up—24/7.' }
];

const eaFaq = (noun = 'calls') => ([
  { q: 'Will it actually sound human?', a: 'Yes—try it above. Pick a role and Alfred calls you in about a minute so you can hear it before you decide.' },
  { q: 'What happens if the AI gets stuck?', a: `Alfred routes to a person, takes a message, or follows your fallback rules. We tune these on real ${noun} before launch so nothing falls through.` },
  { q: 'Does it work with my number and tools?', a: 'Yes. You forward your existing number—no new hardware—and Alfred books into the scheduler and software you already use.' },
  { q: 'How long until it’s live?', a: 'Days, not months. Short discovery call, we build and test on your real call flows, then go live.' },
  { q: 'Is it really done-for-you?', a: 'Yes. No platform to learn—we configure, tune, and manage Alfred for you. You approve how it sounds.' }
]);

// Pricing — LOCKED source: AlfredVoice-v2/docs/pricing.md (2026-06-29).
// Public fields only (no internal margins/cost). Month-to-month; overage $0.35/min.

// SMB (consumer/service verticals)
const SMB_PRICING = [
  {
    name: 'Core', price: '$149/mo', setup: '+ $299 one-time setup',
    headline: 'For getting started with AI voice support.',
    callouts: ['750 minutes/month included', '1 dedicated phone number', 'AI receptionist, booking & routing', 'Spanish included free', 'Email summaries & call analytics'],
    cta: 'Get Started'
  },
  {
    name: 'Professional', price: '$449/mo', setup: '+ $599 one-time setup', featured: true,
    headline: 'For growing businesses with steady call volume.',
    callouts: ['2,000 minutes/month included', 'Up to 3 phone numbers', 'Appointment scheduling + CRM integration', 'Spanish included free', 'Priority support + monthly optimization'],
    cta: 'Get Started'
  },
  {
    name: 'Enterprise', price: '$899/mo', setup: '+ $999 one-time setup',
    headline: 'For high-volume, multi-location operations.',
    callouts: ['4,000 minutes/month included', 'Unlimited phone numbers', 'Custom integrations & branding', 'Dedicated account manager', '24/7 priority support'],
    cta: 'Talk With Sales'
  }
];

// Trucking carriers — driver dispatch (also used for the dispatcher page)
const CARRIER_PRICING = [
  {
    name: 'Low', price: '$499/mo', setup: '+ $499 one-time setup',
    headline: 'Owner-operators & small fleets (≤15 trucks).',
    callouts: ['~1,500 minutes/month', 'Up to ~10 loads/day', 'Per-load driver calls: assign, arrival, departure', '24/7 dispatch coverage'],
    cta: 'Get Started'
  },
  {
    name: 'Medium', price: '$1,399/mo', setup: '+ $599 one-time setup', featured: true,
    headline: 'Growing fleets (16–30 trucks).',
    callouts: ['~4,000 minutes/month', '~15–25 loads/day', 'Driver dispatch + check-ins, 24/7', 'TMS integration wired at setup'],
    cta: 'Get Started'
  },
  {
    name: 'High', price: '$3,299/mo', setup: '+ $799 one-time setup',
    headline: 'Large fleets (31–50 trucks).',
    callouts: ['~8,000 minutes/month', '~30–50 loads/day', 'Full driver dispatch, 24/7', 'Priority support'],
    cta: 'Talk With Sales'
  }
];

// Freight brokers — broker dispatch (higher-reasoning AI)
const BROKER_PRICING = [
  {
    name: 'Low', price: '$799/mo', setup: '+ $999 one-time setup',
    headline: 'Small brokerages (~5–10 loads/day).',
    callouts: ['~2,500 minutes/month', 'Carrier coordination + load check-calls', 'Higher-reasoning AI for negotiation', '24/7 coverage'],
    cta: 'Get Started'
  },
  {
    name: 'Medium', price: '$2,099/mo', setup: '+ $999 one-time setup', featured: true,
    headline: 'Growing brokerages (~15–25 loads/day).',
    callouts: ['~6,000 minutes/month', 'Carrier sourcing + track-and-trace', 'TMS / load board integration at setup', '24/7 coverage'],
    cta: 'Get Started'
  },
  {
    name: 'High', price: '$4,999/mo', setup: '+ $1,299 one-time setup',
    headline: 'High-volume brokerages (~30–50 loads/day).',
    callouts: ['~12,000 minutes/month', 'Full broker dispatch + negotiation support', 'TMS / load board integration at setup', 'Priority support'],
    cta: 'Talk With Sales'
  }
];

// ---------------------------------------------------------------------------
// DENTAL / MEDICAL
// ---------------------------------------------------------------------------
export const dentalConfig = {
  industry: 'Dental',
  heroTag: 'FOR DENTAL & MEDICAL OFFICES',
  heroTitle: 'Never Miss Another New Patient',
  heroSubtitle: 'Alfred answers every call 24/7, books patients on the first ring, and runs your reminders—for a fraction of a front-desk salary.',
  heroImage: '/images/alfred-dental.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers Every Call, 24/7', description: 'Picks up on the first ring so staff stop dropping chairside work.' },
    { title: 'Books Patients in Real Time', description: 'Schedules new patients night, weekend, and lunch—not just messages.' },
    { title: 'Works Without Turnover', description: 'Costs a few days of front-desk pay; never quits, no benefits.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'Front desk is chairside—new-patient calls go to voicemail', solution: 'Alfred answers every call on the first ring, 24/7' },
    { problem: 'Patients call after hours, on weekends, and at lunch', solution: 'Alfred books around the clock—never voicemail' },
    { problem: 'Each new patient is worth thousands in lifetime value', solution: 'Alfred captures and books every new-patient call' },
    { problem: 'New front-desk hires take months to get consistent', solution: 'Alfred coaches staff on your scripts from day one' }
  ],

  integrations: [
    { name: 'Your Scheduling System', description: 'Books and reschedules into your live calendar.' },
    { name: 'RevenueWell', description: 'Automated reminders and patient recalls.' },
    { name: 'Weave', description: 'Front-office communications and reminders.' },
    { name: 'Dental Intel', description: 'Call tracking and conversion analytics.' }
  ],

  features: [
    { title: '24/7 Answering', description: 'Every inbound call answered instantly, zero hold time.', Icon: Clock, benefit: 'Saves Time' },
    { title: 'Real-Time Booking', description: 'Books and reschedules into your live calendar.', Icon: Calendar, benefit: 'Increases Revenue' },
    { title: 'New-Patient Intake', description: 'Qualifies callers, captures insurance and reason for visit.', Icon: TrendingUp, benefit: 'Increases Revenue' },
    { title: 'Outbound Reminders', description: 'Automated confirmations and recalls cut no-shows.', Icon: Phone, benefit: 'Increases Revenue' },
    { title: 'Insurance Deflection', description: 'Answers “do you take my plan?” without tying up staff.', Icon: ShieldCheck, benefit: 'Saves Time' },
    { title: 'Emergency Triage', description: 'Escalates true emergencies to your on-call protocol.', Icon: AlertTriangle, benefit: 'Saves Time' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'Won’t patients want a real person?', a: 'They want a fast answer. Alfred picks up instantly, books the visit, and transfers true emergencies to your team.' },
    { q: 'Will it know my schedule, insurance, and procedures?', a: 'Yes. Alfred is trained on your hours, providers, services, and accepted plans, and books into your live calendar.' },
    { q: 'Is it HIPAA-compliant?', a: 'Yes—built for healthcare front-office use under BAA-appropriate controls. It triages emergencies per your protocol and never gives clinical advice.' }
  ],

  roiCalculator: {
    title: 'What one new patient is worth',
    metrics: [
      { value: '$6,700+', label: 'Lifetime value per new patient' },
      { value: '24/7', label: 'Never miss a call' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'A single new patient can be worth more than a year of Alfred—and he answers and books every call, for a fraction of a front-desk hire.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// MED SPA / AESTHETICS
// ---------------------------------------------------------------------------
export const medspaConfig = {
  industry: 'Med Spa',
  heroTag: 'FOR MED SPAS & AESTHETIC CLINICS',
  heroTitle: 'Never Miss a High-Ticket Lead',
  heroSubtitle: 'Alfred answers every call 24/7, books the appointment, and follows up in seconds—for less than a part-time receptionist.',
  heroImage: '/images/alfred-medspa.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers Every Call, 24/7', description: 'Handles inbound nights and weekends so injectors never stop mid-treatment.' },
    { title: 'Books in Seconds, Not Hours', description: 'Instant callback on web leads wins the booking before competitors call.' },
    { title: 'Covers the Front Desk', description: 'Works lunches, evenings, and holidays—gaps no hire fills.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'Injectors can’t stop mid-treatment to answer calls', solution: 'Alfred answers every call in seconds, 24/7' },
    { problem: 'High-intent web leads sit for hours before a callback', solution: 'Alfred calls every web lead back instantly' },
    { problem: 'No-shows leave injector chairs empty', solution: 'Alfred takes deposits and runs reminders on every booking' },
    { problem: 'Lapsed clients never get a follow-up', solution: 'Alfred re-engages them and pitches memberships' }
  ],

  integrations: [
    { name: 'Vagaro', description: 'Booking, scheduling, and payments.' },
    { name: 'AestheticsPro', description: 'Med spa EMR and membership management.' },
    { name: 'Pabau', description: 'Clinic CRM, scheduling, and records.' },
    { name: 'Curogram', description: 'Patient scheduling and reminders.' }
  ],

  features: [
    { title: 'Always-On Answering', description: 'Picks up every call day or night—never voicemail.', Icon: Clock, benefit: 'Saves Time' },
    { title: 'Instant Lead Callback', description: 'Calls web-form leads back within seconds.', Icon: Zap, benefit: 'Increases Revenue' },
    { title: 'Live Booking', description: 'Books and reschedules in your system on the call.', Icon: Calendar, benefit: 'Increases Revenue' },
    { title: 'Deposit Enforcement', description: 'Collects a deposit at booking to cut no-shows.', Icon: DollarSign, benefit: 'Increases Revenue' },
    { title: 'Reminder Sequence', description: 'Two-touch reminders plus live reschedule recover ghosts.', Icon: Phone, benefit: 'Reduces Expenses' },
    { title: 'Membership Follow-Up', description: 'Re-engages lapsed clients and pitches recurring plans.', Icon: TrendingUp, benefit: 'Reduces Expenses' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'Won’t an AI feel robotic to my clients?', a: 'Alfred competes with your voicemail, not your front desk. It sounds natural, books in real time, and hands clinical questions to your staff.' },
    { q: 'We already have online booking—why this?', a: 'Online booking only catches self-serve clients. Your highest-intent prospects still call to ask “am I a candidate?” Alfred answers and books them.' },
    { q: 'Is it HIPAA-safe and does it fit our scheduler?', a: 'Yes. Alfred is scoped to intake, scheduling, and reminders under BAA-appropriate controls, and books into your existing system.' }
  ],

  roiCalculator: {
    title: 'What one client is worth',
    metrics: [
      { value: '$7,800+', label: 'Client lifetime value' },
      { value: '24/7', label: 'Never miss a lead' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'One high-ticket client can cover months of Alfred—and he answers and sells around the clock, for less than another coordinator.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// VETERINARY
// ---------------------------------------------------------------------------
export const vetConfig = {
  industry: 'Veterinary',
  heroTag: 'FOR VETERINARY CLINICS & HOSPITALS',
  heroTitle: 'Never Miss Another Call. Ever.',
  heroSubtitle: 'Alfred answers every call 24/7, books the appointment, and frees your front desk—done-for-you.',
  heroImage: '/images/alfred-vet.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers Every Call, Zero Hold', description: 'Deflects routine inbound so CSRs escape the phone and stay patient-side.' },
    { title: 'Books Appointments Instantly', description: 'Captures the calls you’re losing and fills your schedule.' },
    { title: 'Replaces Your Answering Service', description: 'Flat monthly cost ends per-call overages and overtime coverage.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'Phones ring while CSRs are with patients', solution: 'Alfred answers instantly, every call, zero queue' },
    { problem: 'After-hours emergencies hit voicemail', solution: 'Alfred triages, reassures, and routes true emergencies 24/7' },
    { problem: 'No-shows drain the daily schedule', solution: 'Alfred runs 48h/24h/morning-of reminders automatically' },
    { problem: 'Receptionist turnover is constant', solution: 'Alfred never quits—consistent service from day one' }
  ],

  integrations: [
    { name: 'ezyVet', description: 'Cloud practice-management bookings sync.' },
    { name: 'Shepherd', description: 'Appointments land directly in your schedule.' },
    { name: 'PetDesk', description: 'Client communication and reminder workflows.' },
    { name: 'Digitail', description: 'All-in-one PIMS scheduling integration.' }
  ],

  features: [
    { title: '24/7 Answering', description: 'Every call answered instantly—day, night, weekend.', Icon: Clock, benefit: 'Saves Time' },
    { title: 'Direct Schedule Booking', description: 'Books and reschedules straight into your software.', Icon: Calendar, benefit: 'Increases Revenue' },
    { title: 'Smart Triage Routing', description: 'Collects intake data, escalates true emergencies to staff.', Icon: AlertTriangle, benefit: 'Saves Time' },
    { title: 'Automated Reminders', description: '48h/24h/morning-of cadence cuts no-shows.', Icon: Phone, benefit: 'Increases Revenue' },
    { title: 'Overflow Coverage', description: 'Absorbs peak rush so techs stay with patients.', Icon: Users, benefit: 'Reduces Expenses' },
    { title: 'FAQ Handling', description: 'Hours, directions, refill and pickup status answered.', Icon: MessageSquare, benefit: 'Saves Time' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'Pet owners want a real person, especially in emergencies.', a: 'Alfred instantly routes true emergencies to your on-call protocol after collecting triage data—so a human is free for the calls that need one.' },
    { q: 'Will it sound robotic or give medical advice?', a: 'Alfred stays scoped to scheduling, FAQs, and triage—never clinical diagnosis. Anything clinical escalates to your team.' },
    { q: 'Will it work with our practice software?', a: 'Yes. Alfred integrates with your scheduling system so bookings land directly in your software—no double entry.' }
  ],

  roiCalculator: {
    title: 'What one new client is worth',
    metrics: [
      { value: '$4,000+', label: 'Lifetime value per pet' },
      { value: '24/7', label: 'Never miss a call' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'A single recovered booking can pay for Alfred many times over—and he answers every call instantly, for less than another CSR.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// HVAC
// ---------------------------------------------------------------------------
export const hvacConfig = {
  industry: 'HVAC',
  heroTag: 'FOR HVAC CONTRACTORS',
  heroTitle: 'Never Miss Another HVAC Call',
  heroSubtitle: 'Alfred answers every call 24/7, books the job, and beats your competitor to the lead—for less than one missed install a month.',
  heroImage: '/images/alfred-hvac.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers Every Call, 24/7', description: 'Handles nights, weekends, and heat-wave surges so the phone stops fragmenting your day.' },
    { title: 'Books the Job, Not a Message', description: 'Qualifies the lead and books into your scheduler in seconds.' },
    { title: 'Replaces Service & CSR Overflow', description: 'Flat-priced intake instead of dispatchers or surge-billed services.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'Peak-season calls come in while every tech is slammed', solution: 'Alfred answers 100% of calls instantly, 24/7' },
    { problem: 'Most summer emergencies come in after 5 PM', solution: 'Alfred takes the night shift—no owner on-call' },
    { problem: 'Homeowners book the first contractor to answer', solution: 'Alfred responds in seconds, so you’re always first' },
    { problem: 'New techs and CSRs take months to ramp', solution: 'Alfred coaches staff on dispatch SOPs from day one' }
  ],

  integrations: [
    { name: 'ServiceTitan', description: 'Books directly into scheduling and dispatch.' },
    { name: 'Housecall Pro', description: 'Live appointment booking, no double entry.' },
    { name: 'Jobber', description: 'Syncs jobs into your calendar instantly.' },
    { name: 'CallRail', description: 'Answers your tracked marketing leads.' }
  ],

  features: [
    { title: 'Instant 24/7 Answer', description: 'Picks up every call—day, night, and weekend.', Icon: Clock, benefit: 'Saves Time' },
    { title: 'Direct Calendar Booking', description: 'Books appointments into your dispatch system live.', Icon: Calendar, benefit: 'Increases Revenue' },
    { title: 'Emergency Triage', description: 'Escalates no-heat/no-cool calls to your on-call tech.', Icon: AlertTriangle, benefit: 'Increases Revenue' },
    { title: 'Lead Qualification', description: 'Screens and captures every caller—nothing lost.', Icon: TrendingUp, benefit: 'Increases Revenue' },
    { title: 'Outbound Follow-Up', description: 'Reminders and callbacks cut no-shows, revive leads.', Icon: Phone, benefit: 'Increases Revenue' },
    { title: 'Flat Monthly Price', description: 'No per-call surge billing when summer hits.', Icon: DollarSign, benefit: 'Reduces Expenses' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'My customers want a real person.', a: 'They want a fast answer, not voicemail. Alfred is a natural conversation that books the job in seconds, 24/7.' },
    { q: 'Will it sound robotic?', a: 'It’s a natural voice trained on your hours, service area, and pricing—answering like one of your best CSRs. Live demo available.' },
    { q: 'Does it integrate with my software?', a: 'Yes. Alfred books directly into ServiceTitan, Housecall Pro, or Jobber—no double entry.' }
  ],

  roiCalculator: {
    title: 'What one job is worth',
    metrics: [
      { value: '1 install', label: 'Can pay for a year of Alfred' },
      { value: '24/7', label: 'Never miss a call' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'A homeowner with a dead AC books the first contractor to answer. Alfred is always first—for less than one missed install a month.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// PLUMBING / HOME SERVICES
// ---------------------------------------------------------------------------
export const plumbingConfig = {
  industry: 'Plumbing',
  heroTag: 'FOR PLUMBERS & HOME-SERVICE PROS',
  heroTitle: 'Never Miss Another Plumbing Job',
  heroSubtitle: 'Alfred answers every call 24/7, books the emergency, and wins the speed-to-lead race that decides who gets the job.',
  heroImage: '/images/alfred-plumbing.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers & Triages Every Call', description: 'Handles FAQs, hours, and “do you service my area?” so you keep wrenching.' },
    { title: 'Books the Emergency Instantly', description: 'Qualifies burst-pipe calls and books into your schedule, day or night.' },
    { title: 'Replaces Your Answering Service', description: 'Does the work of a CSR at a fraction of the cost.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'You’re on a job when the phone rings', solution: 'Alfred answers every call while you work' },
    { problem: 'Most emergencies come in after hours', solution: 'Alfred captures burst-pipe calls overnight' },
    { problem: 'Paid Google leads die in voicemail', solution: 'Alfred answers the calls your ads already paid for' },
    { problem: 'Answering services just take a message', solution: 'Alfred books the job, not a callback list' }
  ],

  integrations: [
    { name: 'ServiceTitan', description: 'Books jobs into scheduling and dispatch.' },
    { name: 'Housecall Pro', description: 'Syncs appointments with your calendar.' },
    { name: 'Jobber', description: 'Captures leads directly into your pipeline.' },
    { name: 'Workiz', description: 'Schedules and dispatches without double entry.' }
  ],

  features: [
    { title: 'Always Answers', description: 'Picks up every call on the first ring, 24/7.', Icon: Clock, benefit: 'Saves Time' },
    { title: 'Emergency Triage', description: 'Qualifies urgency and escalates true emergencies to you.', Icon: AlertTriangle, benefit: 'Increases Revenue' },
    { title: 'Direct Booking', description: 'Books appointments into your calendar—no double entry.', Icon: Calendar, benefit: 'Saves Time' },
    { title: 'Outbound Follow-Up', description: 'Reminders and callbacks cut no-shows, revive leads.', Icon: Phone, benefit: 'Increases Revenue' },
    { title: 'Knows Your Shop', description: 'Tuned with your hours, services, and pricing.', Icon: TrendingUp, benefit: 'Increases Revenue' },
    { title: 'Cost Swap', description: 'Replaces costly month-to-month answering services.', Icon: DollarSign, benefit: 'Reduces Expenses' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'My customers want a real person, especially in an emergency.', a: 'Alfred answers live on the first ring, triages the emergency, and escalates true emergencies to your phone. The alternative is voicemail.' },
    { q: 'I already have a CSR or answering service.', a: 'Keep your CSR for daytime. Alfred covers after-hours and overflow, never puts callers on hold, and books the job instead of taking a message.' },
    { q: 'Won’t AI sound robotic?', a: 'Alfred is tuned for plumbing, knows your pricing, and books real appointments. The real comparison isn’t your best day—it’s voicemail.' }
  ],

  roiCalculator: {
    title: 'What one job is worth',
    metrics: [
      { value: '$350–$1,200', label: 'Value of one service call' },
      { value: '24/7', label: 'Never miss a call' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'In plumbing the job goes to whoever answers first. Alfred answers every call, day or night—for less than one lost job a month.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// ROOFING
// ---------------------------------------------------------------------------
export const roofingConfig = {
  industry: 'Roofing',
  heroTag: 'FOR ROOFING CONTRACTORS',
  heroTitle: 'Never Miss a Five-Figure Roofing Call',
  heroSubtitle: 'Alfred answers every call live, 24/7—books inspections, qualifies leads, and syncs to your CRM, even at 11pm in a hailstorm.',
  heroImage: '/images/alfred-roofing.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers Every Call, 24/7', description: 'Live answering, FAQs, and booking absorb the entire phone load—including storm surges.' },
    { title: 'Books & Qualifies the Lead', description: 'Responds in seconds, hitting the 5-minute rule so you win the job.' },
    { title: 'Replaces the Front-Desk Hire', description: 'Covers nights, weekends, and 120-call storm days without added payroll.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'Storm spikes overwhelm one office manager', solution: 'Alfred scales to any call volume instantly' },
    { problem: 'After-hours storm calls ring to voicemail', solution: 'Alfred answers live, 24/7, no added headcount' },
    { problem: 'Slow follow-up loses the job', solution: 'Alfred books the inspection in seconds' },
    { problem: 'You paid to acquire every one of those leads', solution: 'Alfred captures the marketing spend you already made' }
  ],

  integrations: [
    { name: 'JobNimbus', description: 'Roofing CRM and pipeline management.' },
    { name: 'Jobber', description: 'Scheduling and job management.' },
    { name: 'Housecall Pro', description: 'Field service scheduling and CRM.' },
    { name: 'Roofr', description: 'Roofing estimating and CRM platform.' }
  ],

  features: [
    { title: 'Always-On Receptionist', description: 'Answers live in seconds, 24/7/365—never voicemail.', Icon: Clock, benefit: 'Saves Time' },
    { title: 'Books Inspections', description: 'Schedules estimates directly into your calendar.', Icon: Calendar, benefit: 'Increases Revenue' },
    { title: 'Lead Qualifying', description: 'Screens callers and logs them into your CRM.', Icon: TrendingUp, benefit: 'Increases Revenue' },
    { title: 'Storm-Surge Scaling', description: 'Handles 120 calls in a day without missing one.', Icon: AlertTriangle, benefit: 'Saves Time' },
    { title: 'FAQ Deflection', description: 'Answers hours, service area, insurance, licensing.', Icon: ShieldCheck, benefit: 'Reduces Expenses' },
    { title: 'Outbound Follow-Up', description: 'Re-touches quoted leads before they go cold.', Icon: Phone, benefit: 'Increases Revenue' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'My office manager already handles the phones.', a: 'She’s great—until crews are out and eight calls hit at once, or it’s 9 PM in a hailstorm. Alfred catches the calls she can’t and covers after-hours.' },
    { q: 'Don’t customers want a real person?', a: 'The alternative isn’t a person—it’s voicemail. Alfred answers naturally, qualifies the lead, books the estimate, then hands it to your team.' },
    { q: 'I already have an answering service.', a: 'Does it book the estimate live into your CRM, or just take a message? Does it scale to 120 calls in a storm day? Alfred does.' }
  ],

  roiCalculator: {
    title: 'What one job is worth',
    metrics: [
      { value: '$10k–$30k', label: 'Average roofing job' },
      { value: '24/7', label: 'Never miss a lead' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'You spend hundreds to make the phone ring, then hand the five-figure job to whoever answered. Alfred answers first—costing less than one lost job a year.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// LAW
// ---------------------------------------------------------------------------
export const lawConfig = {
  industry: 'Law',
  heroTag: 'FOR LAW FIRMS & ATTORNEYS',
  heroTitle: 'Never Miss the Call That Signs the Case',
  heroSubtitle: 'Alfred answers every call 24/7, runs intake, books the consult, and feeds your CRM—for a flat monthly rate.',
  heroImage: '/images/alfred-law.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: '24/7 Live Pickup & Intake', description: 'Answers every call, runs your intake script, escalates only what needs you.' },
    { title: 'First-to-Respond Sign-Up', description: 'Qualifies and books the consult instantly, before competitors call back.' },
    { title: 'Flat-Rate Front Desk', description: 'Replaces a metered answering service and defers a reception hire.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'Most firms don’t answer the phone live', solution: 'Alfred picks up on the first ring, every time' },
    { problem: 'Prospects who hit voicemail call the next firm', solution: 'Alfred runs a live intake conversation on every call' },
    { problem: '2 a.m. arrests and weekend accidents ring out', solution: 'Alfred is awake nights and weekends, qualifying and booking' },
    { problem: 'Web leads sit for hours before a reply', solution: 'Alfred follows up instantly and books the consult' }
  ],

  integrations: [
    { name: 'Clio', description: 'Intake and matter details logged automatically.' },
    { name: 'Lawmatics', description: 'Leads pushed into your intake automations.' },
    { name: 'MyCase', description: 'Captured clients synced to your CRM.' },
    { name: 'PracticePanther', description: 'Booked consults routed to practice management.' }
  ],

  features: [
    { title: 'Speed-to-Lead Response', description: 'Answers in minutes, not hours, to win the case.', Icon: Zap, benefit: 'Increases Revenue' },
    { title: 'Practice-Area Intake', description: 'Trained on your case types, jurisdiction, and conflicts.', Icon: TrendingUp, benefit: 'Increases Revenue' },
    { title: 'Calendar Booking', description: 'Books consultations directly into your live calendar.', Icon: Calendar, benefit: 'Saves Time' },
    { title: 'Call Screening', description: 'Screens spam and solicitors, escalates real matters.', Icon: ShieldCheck, benefit: 'Saves Time' },
    { title: 'Outbound Follow-Up', description: 'Consult reminders and callbacks on un-booked leads.', Icon: Phone, benefit: 'Increases Revenue' },
    { title: 'CRM Sync', description: 'Logs contact and matter details instantly to your CRM.', Icon: MessageSquare, benefit: 'Reduces Expenses' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'Is an AI giving legal advice unauthorized practice of law?', a: 'No. Alfred is scoped to intake, qualification, and scheduling. It collects facts, books the consult, and escalates anything substantive to you.' },
    { q: 'What about confidentiality and privilege?', a: 'Alfred does what your front desk does—captures contact and matter details securely, logs them to your CRM, gives no legal advice, forms no attorney-client relationship.' },
    { q: 'My staff handles the phones fine—why this?', a: 'Put Alfred on a tracking line and see how many after-hours and overflow leads you’re capturing that you weren’t before.' }
  ],

  roiCalculator: {
    title: 'What one case is worth',
    metrics: [
      { value: '4–5 figures', label: 'Value of one signed case' },
      { value: '24/7', label: 'Never miss a case' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'You pay heavily per lead, yet most calls go unanswered. Alfred captures the lead your ad dollars already bought—turning vaporized spend into signed cases.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// TRUCKING COMPANIES (mid-size for-hire freight carriers)
// ---------------------------------------------------------------------------
export const truckingConfig = {
  industry: 'Trucking',
  heroTag: 'FOR TRUCKING COMPANIES',
  heroTitle: 'Never Miss Another Load Offer',
  heroSubtitle: 'Alfred answers broker load offers, driver check-ins, and dock scheduling 24/7—so your dispatch team moves more freight without hiring.',
  heroImage: '/images/alfred-trucking.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Always-On Phone Seat', description: 'Answers every load offer and driver check-in while dispatch works freight.' },
    { title: '24/7 Load Capture', description: 'Catches reload and after-hours offers you lose when the line is busy.' },
    { title: 'More Trucks Per Dispatcher', description: 'Offloads repetitive calls so each dispatcher covers more, deferring the next hire.' }
  ],

  stats: FREIGHT_STATS,
  comparison: FREIGHT_COMPARISON,

  painPoints: [
    { problem: 'Dispatcher’s on another line—broker calls the next carrier', solution: 'Alfred answers every inbound offer instantly' },
    { problem: 'Load offers don’t stop at 5 PM', solution: 'Alfred covers nights, weekends, and holidays' },
    { problem: 'Driver check-ins and status calls bury the desk', solution: 'Alfred takes routine check-ins automatically' },
    { problem: 'Slow dock scheduling and missed detention timing', solution: 'Alfred books appointments and timestamps detention' }
  ],

  integrations: [
    { name: 'McLeod', description: 'TMS for dispatch, status, and billing.' },
    { name: 'Truckbase', description: 'Modern TMS for dispatch operations.' },
    { name: 'DAT', description: 'Load board for spot and contract loads.' },
    { name: 'Truckstop', description: 'Load board and rate data.' }
  ],

  features: [
    { title: 'Load Offer Intake', description: 'Answers offers, captures load details and rate.', Icon: Phone, benefit: 'Increases Revenue' },
    { title: 'Driver Check-Ins', description: 'Fields pickup, transit, and delivery check calls.', Icon: CheckCircle, benefit: 'Saves Time' },
    { title: 'After-Hours Coverage', description: 'Captures overnight offers and driver calls.', Icon: Clock, benefit: 'Increases Revenue' },
    { title: 'Dock Appointments', description: 'Calls shippers and receivers to book windows.', Icon: Calendar, benefit: 'Saves Time' },
    { title: 'Outbound Reminders', description: 'Confirms appointments and reminds drivers.', Icon: MessageSquare, benefit: 'Saves Time' },
    { title: 'Live Transfer', description: 'Hands real negotiations straight to your dispatcher.', Icon: Users, benefit: 'Reduces Expenses' }
  ],

  pricingPlans: CARRIER_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'Don’t brokers want to talk to a real person?', a: 'For negotiation, yes—Alfred transfers those live. For offer intake, check calls, and after-hours loads, it’s Alfred versus voicemail and a competitor.' },
    { q: 'We already have a TMS and load boards—won’t this overlap?', a: 'Those organize and track freight; they don’t answer the phone. Alfred is the voice layer your stack is missing, logging into your workflow.' },
    { q: 'Is automated outbound calling a TCPA risk?', a: 'Low. Every call is carrier-to-broker, shipper, or driver—pure B2B—unlike consumer outbound, so exposure is minimal.' }
  ],

  roiCalculator: {
    title: 'What one load is worth',
    metrics: [
      { value: '$150K', label: 'Revenue per truck / year' },
      { value: '24/7', label: 'Load coverage' },
      { value: '1 load', label: 'Can pay for Alfred' }
    ],
    conclusion: 'Capacity is fungible—the moment your dispatcher is busy or gone, the broker dials the next carrier. Alfred captures those offers 24/7, for a fraction of a dispatcher seat.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// FREIGHT DISPATCHERS (independent dispatch services for owner-operators/small fleets)
// ---------------------------------------------------------------------------
export const dispatcherConfig = {
  industry: 'Freight Dispatch',
  heroTag: 'FOR FREIGHT DISPATCHERS',
  heroTitle: 'Cover More Trucks Without Hiring',
  heroSubtitle: 'Alfred makes and answers the dispatch calls—load sourcing, driver check-ins, appointments, after-hours—so you run more trucks with the people you have.',
  heroImage: '/images/alfred-dispatch.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Check-In Autopilot', description: 'Fields driver check-ins and broker status calls so you stop babysitting trucks.' },
    { title: 'Always-On Load Coverage', description: 'Answers and sources load offers fast, day or night, before a competitor does.' },
    { title: 'Dispatcher-Free Scaling', description: 'Cover more trucks per person and defer the next dispatcher hire.' }
  ],

  stats: FREIGHT_STATS,
  comparison: FREIGHT_COMPARISON,

  painPoints: [
    { problem: 'Booking takes 20 minutes; tracking eats hours', solution: 'Alfred runs the check-calls per load for you' },
    { problem: 'Broker calls when your line is busy—load goes elsewhere', solution: 'Alfred answers every inbound offer' },
    { problem: 'Nights and weekends fall on your cell phone', solution: 'Alfred covers after-hours offers and driver calls 24/7' },
    { problem: 'Drivers don’t report a stalled truck in time', solution: 'Alfred makes proactive scheduled check-calls' }
  ],

  integrations: [
    { name: 'DAT', description: 'Pull and cover load board freight.' },
    { name: 'Truckstop', description: 'Source spot loads and capacity.' },
    { name: 'McLeod', description: 'Log calls into your dispatch TMS.' },
    { name: 'Truckbase', description: 'Sync loads and check-call status.' }
  ],

  features: [
    { title: 'Inbound Offer Intake', description: 'Captures load details and offered rate.', Icon: Phone, benefit: 'Increases Revenue' },
    { title: 'Driver Check-Ins', description: 'Takes pickup, transit, and delivery status.', Icon: CheckCircle, benefit: 'Saves Time' },
    { title: 'Appointment Booking', description: 'Calls docks to set pickup/delivery windows.', Icon: Calendar, benefit: 'Saves Time' },
    { title: 'After-Hours Desk', description: 'Covers nights, weekends, and holidays.', Icon: Clock, benefit: 'Increases Revenue' },
    { title: 'POD Chasing', description: 'Confirms delivery and chases proof so you bill faster.', Icon: ShieldCheck, benefit: 'Saves Time' },
    { title: 'Outbound Reminders', description: 'Driver reminders and appointment confirmations.', Icon: MessageSquare, benefit: 'Reduces Expenses' }
  ],

  pricingPlans: CARRIER_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'Will drivers and brokers accept an AI call?', a: 'It’s a brief, polite B2B status call—confirming pickup, location, and ETA. Because it’s carrier-to-broker, not consumer marketing, TCPA exposure is low.' },
    { q: 'I dispatch for owner-operators who hate robo-calls.', a: 'Alfred handles only the routine status pulls and reminders—the repetitive “where are you” calls. You keep the rate negotiation and the relationship.' },
    { q: 'I already use a TMS and load boards.', a: 'Those organize and surface freight; they don’t answer the phone. Alfred is the voice layer—it makes the calls and logs status back into your workflow.' }
  ],

  roiCalculator: {
    title: 'What one dispatcher seat costs',
    metrics: [
      { value: '$52K', label: 'Loaded cost of one dispatcher seat' },
      { value: '7–8', label: 'Check-calls per load, handled' },
      { value: '24/7', label: 'After-hours coverage' }
    ],
    conclusion: 'The phone is your ceiling—booking is easy, but check-calls and after-hours coverage cap how many trucks one person can handle. Alfred makes those calls, so your desk covers far more loads without the next hire.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// FREIGHT BROKERS (small brokerages, 1–10 employees)
// ---------------------------------------------------------------------------
export const brokersConfig = {
  industry: 'Freight Brokerage',
  heroTag: 'FOR FREIGHT BROKERS',
  heroTitle: 'Cover More Loads Without Hiring',
  heroSubtitle: 'Alfred runs your check-calls and track-and-trace by phone—so a 2-person shop covers loads like a 6-person shop.',
  heroImage: '/images/alfred-brokers.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Automated Check-Calls', description: 'Dials drivers to confirm pickup, location, ETA, delivery, and POD.' },
    { title: 'Carrier Sourcing & 24/7 Coverage', description: 'Reps source and book while Alfred covers nights, weekends, and overflow.' },
    { title: 'More Loads Per Employee', description: 'Carry far more loads per rep without the coordinator hire.' }
  ],

  stats: FREIGHT_STATS,
  comparison: FREIGHT_COMPARISON,

  painPoints: [
    { problem: 'Booking takes 20 min; the check-calls after eat the day', solution: 'Alfred makes every check-call so reps keep selling' },
    { problem: 'Owner sourcing, booking, and tracking on one cell phone', solution: 'Alfred takes the tracking grind off your plate' },
    { problem: '5 a.m. pickups and overnight breakdowns go untracked', solution: 'Alfred covers nights and weekends so nothing slips' },
    { problem: 'Carriers don’t self-report—you find out too late', solution: 'Alfred makes proactive, scheduled calls on every load' }
  ],

  integrations: [
    { name: 'Aljex', description: 'TMS for load and carrier management.' },
    { name: 'Tai', description: 'Brokerage TMS and workflow platform.' },
    { name: 'MacroPoint', description: 'Real-time GPS load visibility.' },
    { name: 'Trucker Tools', description: 'Carrier app-based tracking and sourcing.' }
  ],

  features: [
    { title: 'Pickup Confirmation', description: 'Calls the driver to confirm the load was picked up.', Icon: CheckCircle, benefit: 'Saves Time' },
    { title: 'Location & ETA Pulls', description: 'Gets current location and ETA, updates the load.', Icon: Phone, benefit: 'Saves Time' },
    { title: 'Carrier Sourcing Calls', description: 'Outbound capacity outreach so reps cover more lanes.', Icon: TrendingUp, benefit: 'Increases Revenue' },
    { title: 'After-Hours Coverage', description: 'Runs routine check-calls nights and weekends.', Icon: Clock, benefit: 'Increases Revenue' },
    { title: 'POD Chasing', description: 'Confirms delivery and chases proof so invoices ship.', Icon: ShieldCheck, benefit: 'Reduces Expenses' },
    { title: 'Inbound Carrier Calls', description: 'Answers carrier calls so no opportunity is missed.', Icon: MessageSquare, benefit: 'Reduces Expenses' }
  ],

  pricingPlans: BROKER_PRICING,
  howItWorks: eaHowItWorks,

  faq: [
    { q: 'Will drivers actually accept an AI check-call?', a: 'Yes. It’s a fast, polite B2B status call confirming pickup, location, and ETA—consistent, brief, and to the point.' },
    { q: 'Is this legal to call carriers?', a: 'It’s broker-to-carrier, not consumer marketing, so TCPA exposure is low. These are routine business status calls between you and your carriers.' },
    { q: 'I already have a TMS and visibility tool—why Alfred?', a: 'Those store loads and ping carriers who have the app. Alfred calls the small and owner-op carriers who don’t—on schedule—and writes status back.' }
  ],

  roiCalculator: {
    title: 'What one coordinator costs',
    metrics: [
      { value: '$37K', label: 'Loaded cost of a track-and-trace coordinator' },
      { value: '7–8', label: 'Check-calls per load, handled' },
      { value: '24/7', label: 'After-hours coverage' }
    ],
    conclusion: 'Headcount on the phone caps your growth—booking is easy, the check-calls after gate how many loads you carry. Alfred makes those calls for a flat fee, so your team covers a big-shop load count without the next hire.',
    cta: 'Get a Free Demo'
  },

  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// TOTAL — flagship / homepage (industry-agnostic)
// ---------------------------------------------------------------------------
export const totalConfig = {
  industry: 'Business',
  heroTag: 'ALFRED VOICE · VOICE EXECUTIVE ASSISTANT',
  heroTitle: 'Never Lose a Customer to the Phone',
  heroSubtitle: 'Alfred Voice is your Voice Executive Assistant—it answers every call, completes the sale, and trains your team. Done-for-you.',
  heroImage: '/images/alfred-hero.png',
  showTalkToAlfred: true,

  pillarsTitle: 'Three ways Alfred pays off.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers Every Call', description: 'Books, triages, and routes every call, 24/7. No caller hits voicemail.' },
    { title: 'Completes the Sale', description: 'Qualifies, quotes, follows up, and closes—right on the call.' },
    { title: 'Trains & Supports Your Team', description: 'Onboards new hires and answers staff questions from day one.' }
  ],

  stats: [
    { value: 'Instant', label: 'Answer time, 24/7' },
    { value: '10–15×', label: 'Phone vs. web conversion' },
    { value: '3-in-1', label: 'Receptionist · Closer · Trainer' },
    { value: 'Days', label: 'To go live' }
  ],

  comparison: EA_COMPARISON,

  painPoints: [
    { problem: 'Calls go to voicemail when staff are busy or closed', solution: 'Alfred answers every call in seconds, 24/7' },
    { problem: 'Inbound leads never get qualified or followed up', solution: 'Alfred closes the sale and chases every estimate' },
    { problem: 'New hires take months to get productive', solution: 'Alfred coaches your team on scripts and SOPs from day one' },
    { problem: 'You’re paying for a receptionist, closer, and trainer', solution: 'Alfred is all three—for a fraction of the cost' }
  ],

  integrations: [
    { name: 'Your Existing Number', description: 'Forward your line in minutes—no new hardware.' },
    { name: 'Your Scheduling & CRM', description: 'Alfred books and updates the software you use.' },
    { name: 'Email & Text Summaries', description: 'Every call summarized and sent to your team.' },
    { name: '100+ Tools', description: 'Tell us your stack; we’ll confirm the fit.' }
  ],

  featuresTitle: 'Your whole front office, in one voice.',
  featuresSubtitle: 'One always-on assistant for calls, sales, and your team.',
  features: [
    { title: '24/7 Call Answering', description: 'Every call booked, triaged, and routed.', Icon: Clock, benefit: 'Saves Time' },
    { title: 'Sales & Lead Conversion', description: 'Qualifies, quotes, follows up, and closes.', Icon: DollarSign, benefit: 'Increases Revenue' },
    { title: 'Onboarding & Training', description: 'Coaches new hires on scripts and SOPs.', Icon: GraduationCap, benefit: 'Reduces Expenses' },
    { title: 'Internal Communication', description: 'Answers staff questions and shares updates.', Icon: MessageSquare, benefit: 'Reduces Expenses' },
    { title: 'Works With Your Tools', description: 'Books your scheduler, updates your CRM.', Icon: CheckCircle, benefit: 'Saves Time' },
    { title: 'Done-For-You Setup', description: 'We build, test, and launch it. Live in days.', Icon: Zap, benefit: 'Saves Time' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: [
    { title: 'Discovery Call', description: 'We learn how calls come in, how you sell, and how your team works.' },
    { title: 'We Build & Test It', description: 'We configure Alfred on your real call flows; you test until it’s perfect.' },
    { title: 'Go Live in Days', description: 'Forward your number. Alfred answers, sells, and supports your team—24/7.' }
  ],

  faq: eaFaq('calls'),

  roiCalculator: {
    title: 'What Alfred replaces',
    metrics: [
      { value: '3-in-1', label: 'Receptionist, closer & trainer' },
      { value: '24/7', label: 'Never miss a call or lead' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'Most businesses lose revenue to missed calls and slow follow-up. Alfred fixes all three jobs—often paying for itself with a single captured job.',
    cta: 'Get a Free Demo'
  },

  ctaHeading: 'Ready to put your front office on autopilot?',
  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// OFFLINE CONFIGS — kept for re-enabling commented routes. Legacy styling.
// ---------------------------------------------------------------------------

export const pestControlConfig = {
  industry: 'Pest Control',
  heroTitle: 'Fill Every Route, Maximize Every Day',
  heroSubtitle: 'AI phone system for pest control companies. Book treatments, schedule recurring services, and optimize routes automatically.',
  gradient: BRAND_GRADIENT,
  integrations: [
    { name: 'PestPac', description: 'Full route & customer management sync' },
    { name: 'ServSuite', description: 'Automated service scheduling' },
    { name: 'Jobber', description: 'Job creation & customer communication' },
    { name: 'Briostack', description: 'Route optimization & billing' }
  ],
  features: [
    { title: 'Smart Route Filling', description: 'Books appointments based on technician location and availability.', Icon: TrendingUp },
    { title: 'Recurring Renewals', description: 'Convert one-time jobs to recurring revenue.', Icon: Phone },
    { title: 'Emergency Prioritization', description: 'Urgent infestations get same-day booking.', Icon: AlertTriangle },
    { title: 'No-Show Prevention', description: 'Automated reminders keep schedules full.', Icon: CheckCircle },
    { title: 'Upsell Opportunities', description: 'Identifies customers needing extra services.', Icon: Zap },
    { title: 'Service History Access', description: 'Personalized recommendations from history.', Icon: Clock }
  ],
  pricingPlans: SMB_PRICING,
  stats: VERIFIED_STATS,
  painPoints: [
    { problem: 'Technicians driving long distances between sparse jobs', solution: 'AI books appointments by proximity, maximizing density' },
    { problem: 'One-time customers not converting to recurring plans', solution: 'Automated renewal calls convert more to recurring' },
    { problem: 'No-shows leaving gaps in schedules', solution: 'Automated reminders reduce no-shows' },
    { problem: 'Missing upsell opportunities', solution: 'AI flags customers needing extra services' }
  ],
  howItWorks: eaHowItWorks,
  roiCalculator: {
    title: 'Your Pest Control ROI',
    metrics: [
      { value: '1 contract', label: 'Can pay for months of Alfred' },
      { value: '24/7', label: 'Never miss a call' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'Pest control companies recover lost bookings and convert more recurring revenue while optimizing routes.',
    cta: 'Get a Free Demo'
  },
  demoPhone: DEMO_PHONE
};

export const propertyManagementConfig = {
  industry: 'Property Management',
  heroTitle: 'Handle Tenant Calls Without Adding Staff',
  heroSubtitle: 'AI phone system for property managers. Triage maintenance, answer tenant questions, and escalate emergencies 24/7.',
  gradient: BRAND_GRADIENT,
  integrations: [
    { name: 'AppFolio', description: 'Maintenance tickets & tenant communication' },
    { name: 'Buildium', description: 'Work order automation & rent tracking' },
    { name: 'Rent Manager', description: 'Tenant requests & accounting sync' },
    { name: 'Propertyware', description: 'Automated workflows & reporting' }
  ],
  features: [
    { title: 'Maintenance Triage', description: 'Emergency vs. routine, with priority work orders.', Icon: AlertTriangle },
    { title: 'Tenant FAQ Automation', description: 'Answers common rent and policy questions.', Icon: CheckCircle },
    { title: 'Multi-Property Routing', description: 'Routes by address and issue type.', Icon: TrendingUp },
    { title: 'Rent Payment Reminders', description: 'Reduces late payments and collections work.', Icon: Phone },
    { title: 'Prospect Screening', description: 'Qualifies prospects before leasing agents.', Icon: Zap },
    { title: 'After-Hours Coverage', description: 'Escalates emergencies, defers the rest.', Icon: Clock }
  ],
  pricingPlans: SMB_PRICING,
  stats: VERIFIED_STATS,
  painPoints: [
    { problem: 'Overwhelmed by maintenance calls', solution: 'AI handles routine requests and triages urgency' },
    { problem: 'After-hours non-emergency calls', solution: 'AI provides 24/7 support and next-day follow-ups' },
    { problem: 'Same tenant questions repeatedly', solution: 'AI answers FAQ calls about rent and policies' },
    { problem: 'Hard to scale without hiring', solution: 'AI handles unlimited calls across properties' }
  ],
  howItWorks: eaHowItWorks,
  roiCalculator: {
    title: 'Your Property Management ROI',
    metrics: [
      { value: '1 hire', label: 'Saved vs. adding admin staff' },
      { value: '24/7', label: 'Tenant support' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'Property managers handle more units and provide better 24/7 tenant support without adding staff.',
    cta: 'Get a Free Demo'
  },
  demoPhone: DEMO_PHONE
};

export const callCentersConfig = {
  industry: 'Call Centers',
  heroTitle: 'Scale Your Call Center Without Adding Agents',
  heroSubtitle: 'AI-powered overflow handling, after-hours coverage, and intelligent routing. Handle more calls with the same team.',
  heroTag: 'CALL CENTER OPTIMIZATION',
  gradient: BRAND_GRADIENT,
  integrations: [
    { name: 'Five9', description: 'Cloud contact center integration' },
    { name: 'Genesys', description: 'Enterprise routing & analytics' },
    { name: 'Twilio', description: 'Programmable voice & SMS' },
    { name: 'Salesforce', description: 'CRM & ticket management' }
  ],
  features: [
    { title: 'Intelligent Overflow', description: 'Handles overflow when your team is at capacity.', Icon: Phone },
    { title: '24/7 After-Hours', description: 'Extend hours without night-shift costs.', Icon: Clock },
    { title: 'Smart Call Routing', description: 'Pre-qualifies and routes to the right agent.', Icon: Zap },
    { title: 'Reduced Handle Time', description: 'Collects info before transferring to agents.', Icon: TrendingUp },
    { title: 'Multi-Language Support', description: 'Instant support in 100+ languages.', Icon: CheckCircle },
    { title: 'Real-Time Analytics', description: 'Track volume, peaks, and resolution rates.', Icon: TrendingUp }
  ],
  pricingPlans: SMB_PRICING,
  stats: VERIFIED_STATS,
  painPoints: [
    { problem: 'Abandoned calls during peak hours', solution: 'AI handles unlimited concurrent calls' },
    { problem: 'Expensive after-hours coverage', solution: 'AI provides 24/7 support at lower cost' },
    { problem: 'Long hold times', solution: 'AI answers instantly and resolves many calls' },
    { problem: 'Hard to scale for seasonal peaks', solution: 'Scale capacity up or down on demand' }
  ],
  howItWorks: eaHowItWorks,
  roiCalculator: {
    title: 'Your Call Center ROI',
    metrics: [
      { value: '5 agents', label: 'Worth of capacity added' },
      { value: '24/7', label: 'Coverage' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'Call centers handle far more calls with the same team while improving customer satisfaction.',
    cta: 'Get a Free Demo'
  },
  demoPhone: DEMO_PHONE
};

// ---------------------------------------------------------------------------
// ALFRED 346 — the self-demo line (+1 346-258-0006).
//
// Public landing page for Alfred's OWN demo number. The whole pitch is "hear it
// for yourself": the visitor enters their name + number, gives express consent,
// and Alfred calls them back in about a minute from the 346 line — the consent →
// callback flow served by <TalkToAlfred />, which POSTs to VITE_DEMO_CALL_ENDPOINT
// → core /api/demo-call → the 346 "Alfred Voice AI" tenant. showTalkToAlfred:true
// renders the #talk-to-alfred section and the hero "Talk to Alfred Now" anchor
// targets it. Everything else mirrors the standard industries-page styling.
// ---------------------------------------------------------------------------
export const alfred346Config = {
  industry: 'Business',
  heroTag: 'ALFRED VOICE · HEAR IT LIVE',
  heroTitle: 'Don’t read about Alfred. Hear him call you.',
  heroSubtitle: 'Enter your name and number, give consent, and Alfred Voice calls you back in about a minute—from +1 (346) 258-0006. Pick the role you want to hear, and judge it with your own ears.',
  heroImage: '/images/alfred-hero.png',
  showTalkToAlfred: true,
  talkToAlfredTitle: 'Hear Alfred for yourself',
  talkToAlfredSubtitle: 'Pick a role, drop your number, check the consent box, and Alfred calls you in about a minute—from (346) 258-0006.',

  pillarsTitle: 'Three jobs. One Voice Executive Assistant.',
  pillarsSubtitle: 'Reduce expenses. Save time. Increase revenue.',
  pillars: [
    { title: 'Answers Every Call', description: 'Picks up on the first ring, 24/7—books, triages, and routes every call. No caller hits voicemail.' },
    { title: 'Completes the Sale', description: 'Qualifies leads, follows up instantly, and books the meeting—right on the call.' },
    { title: 'Trains & Supports Your Team', description: 'Onboards new hires and answers staff questions from day one.' }
  ],

  stats: VERIFIED_STATS,
  comparison: EA_COMPARISON,

  painPoints: [
    { problem: '“Will an AI sound robotic to my callers?”', solution: 'Stop guessing—have Alfred call you and judge it live' },
    { problem: 'Calls go to voicemail when staff are busy or closed', solution: 'Alfred answers every call in seconds, 24/7' },
    { problem: 'Inbound leads never get qualified or followed up', solution: 'Alfred closes the sale and chases every estimate' },
    { problem: 'You’re paying for a receptionist, closer, and trainer', solution: 'Alfred is all three—for a fraction of the cost' }
  ],

  integrations: [
    { name: 'Your Existing Number', description: 'Forward your line in minutes—no new hardware.' },
    { name: 'Your Scheduling & CRM', description: 'Alfred books and updates the software you use.' },
    { name: 'Email & Text Summaries', description: 'Every call summarized and sent to your team.' },
    { name: '100+ Tools', description: 'Tell us your stack; we’ll confirm the fit.' }
  ],

  featuresTitle: 'What you’ll hear on the call.',
  featuresSubtitle: 'A live demo of the product, demonstrating itself.',
  features: [
    { title: 'Natural Conversation', description: 'Alfred listens, reacts, and talks like a person—no menus, no scripts.', Icon: MessageSquare, benefit: 'Sounds Human' },
    { title: 'Receptionist Mode', description: 'Greets, books appointments, takes messages, and routes calls.', Icon: Phone, benefit: 'Saves Time' },
    { title: 'Sales Mode', description: 'Qualifies the lead, surfaces value, and books the next step.', Icon: DollarSign, benefit: 'Increases Revenue' },
    { title: 'Support Mode', description: 'Answers FAQs and walks callers through troubleshooting.', Icon: Headphones, benefit: 'Reduces Expenses' },
    { title: 'Works With Your Tools', description: 'Books your scheduler, updates your CRM, summarizes every call.', Icon: CheckCircle, benefit: 'Saves Time' },
    { title: 'Done-For-You Setup', description: 'We build, test, and launch it on your real call flows. Live in days.', Icon: Zap, benefit: 'Saves Time' }
  ],

  pricingPlans: SMB_PRICING,
  howItWorks: [
    { title: 'Enter Your Number & Consent', description: 'Pick a role, drop your name and number, and check the consent box.' },
    { title: 'Alfred Calls You Back', description: 'In about a minute, Alfred calls from (346) 258-0006 and opens in the role you chose.' },
    { title: 'Judge It Live', description: 'Talk to Alfred about your business and hear exactly how he’d handle your calls.' }
  ],

  faq: [
    { q: 'What happens when I submit the form?', a: 'Alfred Voice places an automated, AI-powered call to the number you entered—from +1 (346) 258-0006—in about a minute. He opens in the role you picked, and you talk to him live.' },
    { q: 'Is this a real AI or a recording?', a: 'It’s the live product. Alfred is an AI voice agent demonstrating itself in real time—he’ll tell you so early on the call. Talk to him about your business and hear how he’d handle it.' },
    { q: 'Why do you need my consent?', a: 'The call is automated, so we require your express consent (TCPA) before dialing. Consent is not a condition of any purchase, and you can ask Alfred to stop at any time.' },
    { q: 'Will my number get spammed?', a: 'No. We place one demo call when you ask for it, rate-limited per number. We don’t sell your number, and you can opt out of any follow-up.' }
  ],

  roiCalculator: {
    title: 'What Alfred replaces',
    metrics: [
      { value: '3-in-1', label: 'Receptionist, closer & trainer' },
      { value: '24/7', label: 'Never miss a call or lead' },
      { value: '10–15×', label: 'Phone vs. web conversion' }
    ],
    conclusion: 'Most businesses lose revenue to missed calls and slow follow-up. Alfred fixes all three jobs—often paying for itself with a single captured job. Hear it for yourself first.',
    cta: 'Get a Free Demo'
  },

  ctaHeading: 'Heard enough? Let’s set Alfred up for your business.',
  demoPhone: DEMO_PHONE
};
